<app-menu>
  <div class="zm-shell">
    <div class="zm-wrapper">
      <header class="zm-toolbar">
        <div class="zm-title">
          <span class="zm-eyebrow">Rates / Boundary Builder</span>
          <h1>Zone Maker</h1>
        </div>

        <div class="zm-meta-strip">
          <span><strong>{{ boundaryCountryLabel }}</strong> country</span>
          <span><strong>{{ selectedStateList.length }}</strong> {{ regionPluralLabel }}</span>
          <span><strong>{{ loadedZipCount }}</strong> loaded</span>
          <span><strong>{{ selectedZipList.length }}</strong> selected {{ boundaryCodePluralLabel }}</span>
          <span><strong>{{ existingZones.length }}</strong> zones</span>
          <span><strong>{{ previewStats?.square_miles || 0 }}</strong> sq mi</span>
        </div>

        <div class="zm-actions">
          <button type="button" class="zm-button zm-button--ghost" (click)="loadExistingZones(true)" [disabled]="!selectedContactUuid || existingZonesLoading">
            <i class="fa-solid fa-rotate" [class.fa-spin]="existingZonesLoading"></i>
            <span>Zones</span>
          </button>
          <button type="button" class="zm-button zm-button--warning" (click)="previewSelection()" [disabled]="previewLoading || selectedZipList.length === 0">
            <i class="fa-solid fa-draw-polygon" [class.fa-beat-fade]="previewLoading"></i>
            <span>{{ previewLoading ? 'Previewing' : 'Preview' }}</span>
          </button>
          <button type="button" class="zm-button zm-button--accent" (click)="saveZone()" [disabled]="!canSave">
            <i class="fa-solid fa-floppy-disk" [class.fa-beat-fade]="saving"></i>
            <span>{{ saving ? 'Saving' : (isEditingZone ? 'Update' : 'Create') }}</span>
          </button>
        </div>
      </header>

      <main class="zm-layout" [style.--zm-sidebar-width.px]="sidebarWidth">
        <aside class="zm-sidebar">
          <section class="zm-panel zm-panel--target">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">Target</span>
                <h2>{{ contactScopeLabel }}</h2>
              </div>
              <div class="zm-toggle">
                <button type="button" [class.is-active]="zoneType === 'customer'" (click)="onZoneTypeChange('customer')">Customer</button>
                <button type="button" [class.is-active]="zoneType === 'carrier'" (click)="onZoneTypeChange('carrier')">Carrier</button>
              </div>
            </div>

            <div class="zm-field-grid">
              <div class="zm-field zm-field--wide">
                <label for="contact-search">Search</label>
                <input id="contact-search" type="text" autocomplete="off" [value]="contactSearchTerm" (input)="onContactSearchChange($any($event.target).value)">
                <div class="zm-autocomplete" *ngIf="contactOptions.length > 0">
                  <button type="button" *ngFor="let option of contactOptions" (click)="selectContact(option)">
                    <strong>{{ option.label }}</strong>
                    <small>{{ option.value }}</small>
                  </button>
                </div>
              </div>
              <div class="zm-field zm-field--wide">
                <label for="contact-uuid">UUID</label>
                <div class="zm-inline-input">
                  <input id="contact-uuid" type="text" [value]="uuidDraft" (input)="uuidDraft = $any($event.target).value" (keyup.enter)="applyUuidDraft()">
                  <button type="button" (click)="applyUuidDraft()">Apply</button>
                </div>
              </div>
            </div>
            <div class="zm-selected-contact" [title]="selectedContactLabel">{{ selectedContactLabel }}</div>
          </section>

          <section class="zm-panel">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">Load Boundaries In</span>
                <h2>{{ regionTitle }}</h2>
              </div>
              <div class="zm-region-actions">
                <div class="zm-toggle zm-toggle--small">
                  <button type="button" [class.is-active]="boundaryCountry === 'USA'" (click)="onBoundaryCountryChange('USA')">USA</button>
                  <button type="button" [class.is-active]="boundaryCountry === 'CAN'" (click)="onBoundaryCountryChange('CAN')">CAN</button>
                </div>
                <div class="zm-mini-actions">
                  <button type="button" (click)="selectAllStates()" [disabled]="statesLoading || !stateOptions.length">All</button>
                  <button type="button" (click)="clearStates()" [disabled]="!selectedStateList.length">Clear</button>
                </div>
              </div>
            </div>
            <div class="zm-state-summary">{{ selectedStateSummary }}</div>
            <div class="zm-state-grid">
              <button
                type="button"
                class="zm-state-chip"
                *ngFor="let state of stateOptions"
                [class.is-active]="isStateSelected(state.state_abbr)"
                [title]="state.state_name + ' / ' + state.zip_count + ' ' + boundaryCodePluralLabel"
                (click)="toggleState(state.state_abbr)"
              >
                <strong>{{ state.state_abbr }}</strong>
                <small>{{ state.zip_count }}</small>
              </button>
            </div>
          </section>

          <section class="zm-panel zm-panel--zones">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">{{ selectedContactUuid ? 'Contact Zones' : 'No Contact' }}</span>
                <h2>Zones</h2>
              </div>
              <div class="zm-mini-actions">
                <button type="button" (click)="startNewZone()">New</button>
              </div>
            </div>

            <div class="zm-zone-list" *ngIf="existingZones.length; else noZones">
              <button
                type="button"
                class="zm-zone-row"
                *ngFor="let zone of existingZones"
                [class.is-active]="zone.zone_uuid === editingZoneUuid"
                (click)="loadZoneForEditing(zone)"
              >
                <span class="zm-swatch" [style.background]="zoneColorForZone(zone)"></span>
                <span class="zm-zone-main" [title]="zone.zone_uuid">
                  <strong>{{ zone.zone_name }}</strong>
                  <small>{{ zone.zone_id || 'No ID' }} / {{ zone.country }}</small>
                  <small class="zm-zone-uuid">{{ zone.zone_uuid }}</small>
                </span>
                <span class="zm-zone-count">{{ zone.postal_code_count }}</span>
              </button>
            </div>
            <ng-template #noZones>
              <div class="zm-empty">{{ existingZonesLoading ? 'Loading zones' : 'Select a contact to load zones' }}</div>
            </ng-template>
          </section>

          <section class="zm-panel">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">{{ isEditingZone ? 'Editing' : 'Draft' }}</span>
                <h2>Zone Details</h2>
              </div>
              <span class="zm-mode-pill">{{ isEditingZone ? 'Update' : 'Create' }}</span>
            </div>

            <div class="zm-field-grid zm-field-grid--zone">
              <div class="zm-field zm-field--wide" *ngIf="editingZoneUuid">
                <label for="zone-uuid">Zone UUID</label>
                <input id="zone-uuid" type="text" [value]="editingZoneUuid" readonly>
              </div>
              <div class="zm-field zm-field--wide">
                <label for="zone-name">Name</label>
                <input id="zone-name" type="text" [value]="zoneName" (input)="zoneName = $any($event.target).value">
              </div>
              <div class="zm-field">
                <label for="zone-id">ID</label>
                <input id="zone-id" type="text" [value]="zoneId" (input)="zoneId = $any($event.target).value">
              </div>
              <div class="zm-field">
                <label for="zone-country">Country</label>
                <input id="zone-country" type="text" maxlength="3" [value]="boundaryCountry" readonly>
              </div>
              <div class="zm-field zm-field--wide zm-cleanup-control">
                <label>Boundary Cleanup</label>
                <div class="zm-toggle">
                  <button type="button" [class.is-active]="boundaryCleanupEnabled" (click)="setBoundaryCleanupEnabled(true)">On</button>
                  <button type="button" [class.is-active]="!boundaryCleanupEnabled" (click)="setBoundaryCleanupEnabled(false)">Off</button>
                </div>
              </div>
              <div class="zm-field">
                <label for="zone-gap-fill">Close Gaps Mi</label>
                <input id="zone-gap-fill" type="number" min="0" max="5" step="0.05" [disabled]="!boundaryCleanupEnabled" [value]="gapFillMiles" (input)="onGapFillMilesChange($any($event.target).value)">
              </div>
              <div class="zm-field">
                <label for="zone-hole-fill">Fill Holes Sq Mi</label>
                <input id="zone-hole-fill" type="number" min="0" max="100" step="0.05" [disabled]="!boundaryCleanupEnabled" [value]="cleanHoleSquareMiles" (input)="onCleanHoleSquareMilesChange($any($event.target).value)">
              </div>
              <div class="zm-field zm-field--wide zm-cleanup-action">
                <button type="button" class="zm-button zm-button--warning" (click)="previewSelection()" [disabled]="!boundaryCleanupEnabled || previewLoading || !selectedZipList.length">
                  <i class="fa-solid fa-broom" [class.fa-spin]="previewLoading"></i>
                  <span>Clean Boundary</span>
                </button>
              </div>
              <div class="zm-field zm-field--wide" *ngIf="zoneType === 'customer'">
                <label for="zone-description">Description</label>
                <textarea id="zone-description" [value]="description" (input)="description = $any($event.target).value"></textarea>
              </div>
            </div>
          </section>

          <section class="zm-panel">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">Grouping Helper</span>
                <h2>Radius</h2>
              </div>
              <div class="zm-toggle zm-toggle--small">
                <button type="button" [class.is-active]="radiusMergeMode === 'add'" (click)="radiusMergeMode = 'add'">Add</button>
                <button type="button" [class.is-active]="radiusMergeMode === 'replace'" (click)="radiusMergeMode = 'replace'">Replace</button>
              </div>
            </div>
            <div class="zm-radius-grid">
              <div class="zm-field">
                <label for="radius-zip">Center {{ boundaryCodeLabel }}</label>
                <input id="radius-zip" type="text" [attr.inputmode]="boundaryCountry === 'USA' ? 'numeric' : 'text'" [value]="radiusCenterZip" (input)="radiusCenterZip = $any($event.target).value" (keyup.enter)="applyRadiusFromZip()">
              </div>
              <div class="zm-field">
                <label for="radius-miles">Miles</label>
                <input id="radius-miles" type="number" min="1" max="500" step="1" [value]="radiusMiles" (input)="radiusMiles = +$any($event.target).value">
              </div>
              <button type="button" class="zm-button zm-button--primary" (click)="applyRadiusFromZip()" [disabled]="radiusLoading">
                <i class="fa-solid fa-bullseye"></i><span>{{ boundaryCodeLabel }}</span>
              </button>
              <button type="button" class="zm-button zm-button--ghost" (click)="applyRadiusFromMapCenter()" [disabled]="radiusLoading">
                <i class="fa-solid fa-crosshairs"></i><span>Map</span>
              </button>
            </div>
          </section>

          <section class="zm-panel zm-panel--selection">
            <div class="zm-panel-head">
              <div>
                <span class="zm-eyebrow">Workspace</span>
                <h2>Selected {{ boundaryCodePluralLabel }}</h2>
              </div>
              <div class="zm-mini-actions">
                <button type="button" (click)="clearSelection()" [disabled]="!selectedZipList.length">Clear</button>
              </div>
            </div>
            <div class="zm-zip-chip-list" *ngIf="selectedZipList.length; else noSelectedZips">
              <button type="button" class="zm-zip-chip" *ngFor="let postalCode of visibleSelectedZipList" (click)="removeSelectedZip(postalCode)">
                {{ postalCode }} <i class="fa-solid fa-xmark"></i>
              </button>
              <span class="zm-more-chip" *ngIf="selectedZipList.length > visibleSelectedZipList.length">+{{ selectedZipList.length - visibleSelectedZipList.length }}</span>
            </div>
            <ng-template #noSelectedZips>
              <div class="zm-empty">Click shapes, use visible tools, or run a radius.</div>
            </ng-template>
          </section>
        </aside>

        <button type="button" class="zm-resize-handle" [class.is-active]="resizingSidebar" (mousedown)="onSidebarResizeStart($event)" title="Resize side panel" aria-label="Resize side panel"></button>

        <section class="zm-map-panel">
          <div class="zm-map-toolbar">
            <div class="zm-field zm-map-search">
              <label for="zip-search">{{ boundaryCodeLabel }} Search</label>
              <div class="zm-inline-input">
                <input id="zip-search" type="text" [attr.inputmode]="boundaryCountry === 'USA' ? 'numeric' : 'text'" autocomplete="off" [value]="zipSearchTerm" (input)="onZipSearchChange($any($event.target).value)" (keyup.enter)="focusZip(zipSearchTerm, true)">
                <button type="button" (click)="focusZip(zipSearchTerm, true)" [disabled]="zipSearchLoading"><i class="fa-solid fa-magnifying-glass"></i></button>
              </div>
              <div class="zm-autocomplete zm-autocomplete--map" *ngIf="zipSearchResults.length > 0">
                <button type="button" *ngFor="let zip of zipSearchResults" (click)="focusZip(zip.postal_code, true)">
                  <strong>{{ zip.postal_code }} / {{ zip.state_abbr }}</strong>
                  <small>{{ zip.state_name }}</small>
                </button>
              </div>
            </div>

            <div class="zm-map-actions">
              <button type="button" class="zm-button zm-button--ghost" (click)="selectVisibleZips()" [disabled]="!loadedZipCodes.length"><i class="fa-solid fa-plus"></i><span>Visible</span></button>
              <button type="button" class="zm-button zm-button--ghost" (click)="removeVisibleZips()" [disabled]="!loadedZipCodes.length"><i class="fa-solid fa-minus"></i><span>Visible</span></button>
              <button type="button" class="zm-button zm-button--ghost" (click)="loadExistingZones(true)" [disabled]="!selectedContactUuid"><i class="fa-solid fa-layer-group"></i><span>Fit Zones</span></button>
            </div>
          </div>

          <div class="zm-map-wrap">
            <div #mapBox class="zone-maker-map"></div>

            <div class="zm-map-status zm-map-status--loading" *ngIf="loadingZips">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              <span>Loading {{ boundaryCodePluralLabel }}</span>
            </div>

            <div class="zm-map-footer">
              <span><strong>{{ selectedStateList.length }}</strong> {{ regionPluralLabel }}</span>
              <span><strong>{{ loadedZipCount }}</strong> {{ boundaryCodePluralLabel }} loaded</span>
              <span *ngIf="zipsLimited" class="is-warn">Zoom in for more detail</span>
            </div>

            <div class="zm-zone-legend" [class.is-collapsed]="zoneLegendCollapsed" *ngIf="existingZones.length">
              <div class="zm-zone-legend-head">
                <div class="zm-eyebrow">Zones</div>
                <button
                  type="button"
                  class="zm-zone-legend-toggle"
                  [title]="zoneLegendCollapsed ? 'Show zones' : 'Hide zones'"
                  [attr.aria-label]="zoneLegendCollapsed ? 'Show zones' : 'Hide zones'"
                  (click)="toggleZoneLegendCollapsed()"
                >
                  <i class="fa-solid" [class.fa-chevron-left]="zoneLegendCollapsed" [class.fa-chevron-right]="!zoneLegendCollapsed"></i>
                </button>
              </div>
              <div class="zm-zone-legend-list" *ngIf="!zoneLegendCollapsed">
                <button type="button" *ngFor="let zone of existingZones" [class.is-active]="zone.zone_uuid === editingZoneUuid" (click)="loadZoneForEditing(zone)">
                  <span class="zm-swatch" [style.background]="zoneColorForZone(zone)"></span>
                  <strong>{{ zone.zone_name }}</strong>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</app-menu>